import cookies from 'js-cookie';
import { redirectToUrl, replaceUrl } from 'app/functions/environment';
import {
  getUrlParamsFromSearchPayload,
  isGoogleAnalyticsAvailable,
  ParamsSearchPayload,
  webtrekkValuesAvailable,
} from 'app/functions/general';
import { getCallbacksResult } from 'app/storage/parameterCallbacks';
import { SearchMaskMountOptions } from '@webc/meeseeks-ui-sdk';
import { shopUrls } from 'app/config/shopUrls';
import { getQueryString } from 'app/functions/environment';
import { getLocaleForGlobalTrips } from 'app/functions/general/getLocaleForGlobalTrips';

export const redirectToSearchResultsPage = (
  payload: ParamsSearchPayload,
  options: SearchMaskMountOptions,
  features: {
    [key: string]: boolean;
  },
  brand: 'flixbus' | 'kamil_koc' | 'greyhound' = 'flixbus',
  isGlobalTripSearch?: boolean,
) => {
  // only if its enabled
  if (options.handleOwnRequestSearchEvent !== true) return;
  const redirectLocale = getLocaleForGlobalTrips(payload.locale);
  const getRedirectUrl = () => {
    if (shopUrls[brand]?.[redirectLocale]) {
      return shopUrls[brand][redirectLocale];
    }
    return shopUrls['flixbus'][redirectLocale];
  };

  const shopUrl = shopUrls[brand]?.[payload.locale]
    ? shopUrls[brand][payload.locale]
    : shopUrls['flixbus'][payload.locale];

  const host = isGlobalTripSearch
    ? getRedirectUrl()
    : options.searchResultsHost || shopUrl || window.location.hostname;
  const urlParams = getUrlParamsFromSearchPayload(payload);

  // FlixTrain flag
  if (options.flixTrain) {
    urlParams.set('origin_flix_train', '1');
  }

  // BackRide flag
  if (urlParams.get('backRideDate')) {
    urlParams.set('backRide', '1');
  }

  // pass feature flags along
  if (Object.keys(features).length > 0) {
    Object.keys(features).forEach((featureName) => {
      urlParams.set(`features[${featureName}]`, features[featureName] ? '1' : '0');
    });
  }

  // pass search host params
  if (options.searchResultsHostParams && options.searchResultsHostParams.length > 0) {
    options.searchResultsHostParams.forEach((param: any) => {
      if (param[0] && param[1]) {
        urlParams.set(param[0], param[1]);
      }
    });
  }

  // Deeplink tracking
  const defaultUrlParams = new URLSearchParams(getQueryString());
  const paramKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gad_source',
    'gclid',
    'uc_disabled',
    'cc_disabled',
  ];
  paramKeys.forEach((key) => {
    const value = defaultUrlParams.get(key);
    if (value) {
      urlParams.set(key, value);
    }
  });

  const disableWafCaptcha = cookies.get('disable_waf_captcha');
  if (disableWafCaptcha) {
    urlParams.set('disable_waf_captcha', disableWafCaptcha);
  }

  // Webtrekk parameters
  if (webtrekkValuesAvailable()) {
    urlParams.set('wt_eid', window.wt.eid);
    urlParams.set('wt_t', Date.now().toString());

    // affiliate code
    const affiliateCookie = cookies.get('affiliate');
    if (affiliateCookie) {
      urlParams.set('affiliate', affiliateCookie);
    }

    // google analytics clientId
    if (isGoogleAnalyticsAvailable()) {
      // GA returns a string value to be concatenated directly to the url, like:
      // _ga=2.101072776.821422058.1523018101-1425581033.1523018101
      const gaQueryString = window.ga.getByName('gaGlobal').get('linkerParam');

      // Need to parse the google query string and extract only the value
      const gaUrlParams = new URLSearchParams(gaQueryString);
      urlParams.set('_ga', gaUrlParams.get('_ga') || '');
    }
  }

  const extraParameters = getCallbacksResult(payload, options);

  Object.keys(extraParameters).forEach((parameterKey) => {
    if (parameterKey !== 'departureCity' && parameterKey !== 'arrivalCity') {
      urlParams.set(parameterKey, extraParameters[parameterKey]);
    }
  });

  if (isGlobalTripSearch) {
    urlParams.set('_locale', redirectLocale);
  }

  const path = 'search';
  const urlToRedirectTo = `${window.location.protocol}//${host}/${path}?${urlParams.toString()}`;
  if (!options.onePageSearch || isGlobalTripSearch) {
    redirectToUrl(urlToRedirectTo);
  } else {
    replaceUrl(
      `${window.location.protocol}//${host}${window.location.pathname}?${urlParams.toString()}`,
    );
  }
};
